import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen, Calculator, Languages, Trophy, Target, Brain,
  Clock, TrendingUp, ChevronRight, Award, Flame, Star
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';

export function DashboardPage() {
  const { user } = useAuthStore();

  const quickActions = [
    { icon: <Calculator className="w-5 h-5" />, label: '数学练习', path: '/subjects/math', color: 'from-blue-500 to-cyan-500' },
    { icon: <Languages className="w-5 h-5" />, label: '英语练习', path: '/subjects/english', color: 'from-emerald-500 to-teal-500' },
    { icon: <Target className="w-5 h-5" />, label: 'NAPLAN模拟', path: '/exams/naplan', color: 'from-purple-500 to-indigo-500' },
    { icon: <Brain className="w-5 h-5" />, label: '错题本', path: '/mistakes', color: 'from-pink-500 to-rose-500' },
  ];

  const recentTopics = [
    { name: '二次方程', subject: '数学', progress: 75, color: 'bg-blue-500' },
    { name: '阅读理解', subject: '英语', progress: 60, color: 'bg-emerald-500' },
    { name: '几何证明', subject: '数学', progress: 45, color: 'bg-blue-500' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
            你好，{user?.name || '同学'}！👋
          </h1>
          <p className="text-slate-600 mt-1">准备好今天的学习了吗？</p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { icon: <Flame className="w-6 h-6" />, label: '连续学习', value: '7 天', color: 'text-orange-500', bg: 'bg-orange-50' },
            { icon: <Star className="w-6 h-6" />, label: '总积分', value: '1,250', color: 'text-amber-500', bg: 'bg-amber-50' },
            { icon: <Award className="w-6 h-6" />, label: '获得徽章', value: '5 个', color: 'text-purple-500', bg: 'bg-purple-50' },
            { icon: <TrendingUp className="w-6 h-6" />, label: '本周进步', value: '+15%', color: 'text-emerald-500', bg: 'bg-emerald-50' },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100"
            >
              <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center ${stat.color} mb-3`}>
                {stat.icon}
              </div>
              <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
              <div className="text-sm text-slate-500">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
            >
              <h2 className="text-lg font-semibold text-slate-900 mb-4">快速开始</h2>
              <div className="grid grid-cols-2 gap-3">
                {quickActions.map((action) => (
                  <Link
                    key={action.path}
                    to={action.path}
                    className="group flex items-center gap-3 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className={`w-10 h-10 bg-gradient-to-br ${action.color} rounded-xl flex items-center justify-center text-white shadow-lg`}>
                      {action.icon}
                    </div>
                    <span className="font-medium text-slate-700 group-hover:text-slate-900">
                      {action.label}
                    </span>
                  </Link>
                ))}
              </div>
            </motion.div>

            {/* Recent Progress */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">最近学习</h2>
                <Link to="/subjects" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                  查看全部
                </Link>
              </div>
              <div className="space-y-4">
                {recentTopics.map((topic) => (
                  <div key={topic.name} className="flex items-center gap-4">
                    <div className={`w-2 h-10 ${topic.color} rounded-full`} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-slate-800">{topic.name}</span>
                        <span className="text-sm text-slate-500">{topic.progress}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${topic.color} rounded-full transition-all`}
                          style={{ width: `${topic.progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Today's Challenge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 text-white"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">每日挑战</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">完成10道数学题</h3>
                  <p className="text-indigo-200 text-sm">完成后可获得50积分和特殊徽章</p>
                </div>
                <Link
                  to="/challenges"
                  className="flex items-center gap-1 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
                >
                  开始
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span>进度</span>
                  <span>3/10</span>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full w-[30%] bg-white rounded-full" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">勋章墙</h2>
                <Link to="/badges" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                  查看全部
                </Link>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {['🏆', '⭐', '🔥', '💎', '🎯', '📚', '🚀', '🌟'].map((emoji, i) => (
                  <div
                    key={i}
                    className={`aspect-square rounded-xl flex items-center justify-center text-2xl ${
                      i < 5 ? 'bg-amber-50' : 'bg-slate-100 opacity-50'
                    }`}
                  >
                    {emoji}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Leaderboard Preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">排行榜</h2>
                <Link to="/leaderboard" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                  查看全部
                </Link>
              </div>
              <div className="space-y-3">
                {[
                  { rank: 1, name: '小明', points: 2850, medal: '🥇' },
                  { rank: 2, name: '小红', points: 2720, medal: '🥈' },
                  { rank: 3, name: '小华', points: 2650, medal: '🥉' },
                ].map((player) => (
                  <div key={player.rank} className="flex items-center gap-3">
                    <span className="text-xl">{player.medal}</span>
                    <div className="flex-1">
                      <div className="font-medium text-slate-800">{player.name}</div>
                      <div className="text-sm text-slate-500">{player.points} 积分</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Study Timer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-gradient-to-br from-rose-500 to-orange-500 rounded-2xl p-6 text-white"
            >
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5" />
                <span className="font-medium">番茄钟</span>
              </div>
              <p className="text-rose-100 text-sm mb-4">专注学习25分钟，休息5分钟</p>
              <Link
                to="/pomodoro"
                className="block w-full py-3 bg-white/20 hover:bg-white/30 rounded-xl text-center font-medium transition-colors"
              >
                开始专注
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
